import type React from "react";

import CheckIcon from "@mui/icons-material/Check";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import styled, { useTheme } from "styled-components";

import { BLUE, DARK_BLUE, RED } from "theme/colors";

import { MUIErrorText } from "../StyledInputs/MUIStyledInputs";

interface Props {
  readonly disabled?: boolean;
  readonly error?: string;
  readonly label: string;
  readonly name: string;
  readonly required?: boolean;
  readonly reverse?: boolean;
  readonly checked?: boolean;
  readonly unregistered?: boolean;
  readonly onChange?: (value: boolean) => void;
}

const getUncheckedBorderColor = (disabled: boolean, hasError: boolean) => {
  if (disabled) return DARK_BLUE[30];
  if (hasError) return RED[100];
  return DARK_BLUE[40];
};

const UncheckedIcon = ({ hasError, disabled }: { hasError: boolean; disabled?: boolean }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      borderRadius: "4px",
      border: `1px solid ${getUncheckedBorderColor(!!disabled, hasError)}`,
      backgroundColor: disabled ? DARK_BLUE[10] : "#fff",
    }}
  />
);

const getCheckedColor = (disabled: boolean) => (disabled ? DARK_BLUE[30] : BLUE[100]);

const CheckedIcon = ({ disabled }: { disabled?: boolean }) => {
  const color = getCheckedColor(!!disabled);
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "4px",
        border: `1px solid ${color}`,
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckIcon sx={{ color: "#fff", fontSize: 18 }} />
    </Box>
  );
};

export const MUICheckBox: React.FC<Props> = ({
  checked,
  disabled,
  error,
  label,
  name,
  required,
  reverse,
  unregistered,
  onChange,
}) => {
  const formContext = useFormContext();
  const control = unregistered ? undefined : formContext?.control;
  const hasError = !!error;
  const theme = useTheme();

  const renderCheckbox = (value: boolean, handleChange: (v: boolean) => void) => (
    <Checkbox
      data-testid={`checkbox-${name}`}
      disableRipple
      checked={value}
      disabled={disabled}
      icon={<UncheckedIcon hasError={hasError} disabled={disabled} />}
      checkedIcon={<CheckedIcon disabled={disabled} />}
      onChange={(_event, newValue) => handleChange(newValue)}
      sx={{
        padding: 0,
        "&:hover": { backgroundColor: "transparent" },
      }}
    />
  );

  return (
    <div>
      <FormControlLabel
        control={
          unregistered ? (
            renderCheckbox(!!checked, value => onChange?.(value))
          ) : (
            <Controller
              control={control}
              name={name}
              rules={{ required }}
              render={({ field }) =>
                renderCheckbox(!!field.value, value => {
                  field.onChange(value);
                  onChange?.(value);
                })
              }
            />
          )
        }
        disabled={disabled}
        labelPlacement={reverse ? "start" : "end"}
        label={label}
        name={name}
        sx={{
          width: "100%",
          alignItems: "center",
          paddingLeft: "11px",
          "& .MuiFormControlLabel-label": {
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            userSelect: "none",
            marginLeft: "6px",
            color: theme.colors.redesign.db100,
          },
          "&.Mui-disabled .MuiTypography-root": {
            color: theme.colors.redesign.db30,
          },
        }}
      />
      {hasError && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

const ErrorText = styled.div`
  ${MUIErrorText}
  margin-top: 5px;
`;
